'use client'

/**
 * Migration Status Hook
 *
 * Detects whether the user has migrated from Supabase to Automerge.
 * Used to show migration prompts and route users appropriately.
 *
 * Checks:
 * 1. localStorage 'storage-backend' flag (fast, same device)
 * 2. localStorage document URL (same device, set by useDocumentUrlSync)
 * 3. Supabase profile document URL (cross-device detection, checked once)
 *
 * Listens for cross-tab storage events; no polling.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { getAutomergeDocumentUrl } from './actions'
import { getEnvironmentStorageKey } from '@/lib/environment'

const STORAGE_BACKEND_KEY = 'storage-backend'
const MIGRATION_DISMISSED_KEY = 'migration-prompt-dismissed'

interface MigrationStatus {
  /** Whether user has migrated to Automerge */
  hasMigrated: boolean
  /** Whether user dismissed migration prompt */
  isDismissed: boolean
  /** Function to dismiss migration prompt */
  dismissPrompt: () => void
  /** Loading state */
  loading: boolean
}

/**
 * Hook to check if user has migrated from Supabase to Automerge.
 *
 * Returns migration status and helper to dismiss the prompt.
 *
 * Migration detection:
 * - Checks localStorage for 'storage-backend' flag (same device)
 * - Checks Supabase profile for document URL (cross-device)
 * - If either exists, user has migrated
 *
 * @returns Migration status object
 *
 * @example
 * ```tsx
 * function MigrationBanner() {
 *   const { hasMigrated, isDismissed, dismissPrompt, loading } = useMigrationStatus()
 *
 *   if (loading || hasMigrated || isDismissed) return null
 *
 *   return (
 *     <div>
 *       <p>Migrate to local storage</p>
 *       <button onClick={dismissPrompt}>Dismiss</button>
 *     </div>
 *   )
 * }
 * ```
 */
export function useMigrationStatus(): MigrationStatus {
  // Check localStorage first (fast, synchronous)
  // Check both the storage-backend flag AND the document URL
  const [hasMigrated, setHasMigrated] = useState(() => {
    if (typeof window === 'undefined') return false

    // Check explicit migration flag
    if (localStorage.getItem(STORAGE_BACKEND_KEY) === 'automerge') {
      return true
    }

    // Also check if document URL exists (user migrated on another device)
    const docUrlKey = getEnvironmentStorageKey('automerge-doc-url')
    const hasDocUrl = !!localStorage.getItem(docUrlKey)

    return hasDocUrl
  })

  const [isDismissed, setIsDismissed] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem(MIGRATION_DISMISSED_KEY) === 'true'
  })

  const [loading, setLoading] = useState(true)

  // Listen for localStorage changes (from useDocumentUrlSync)
  useEffect(() => {
    const checkLocalStorage = () => {
      const docUrlKey = getEnvironmentStorageKey('automerge-doc-url')
      const hasDocUrl = !!localStorage.getItem(docUrlKey)
      const hasBackendFlag =
        localStorage.getItem(STORAGE_BACKEND_KEY) === 'automerge'

      if (hasDocUrl || hasBackendFlag) {
        setHasMigrated(true)
        // Set backend flag if not already set
        if (!hasBackendFlag) {
          localStorage.setItem(STORAGE_BACKEND_KEY, 'automerge')
        }
      }
    }

    // Check immediately
    checkLocalStorage()

    // Listen for changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      const docUrlKey = getEnvironmentStorageKey('automerge-doc-url')
      if (e.key === STORAGE_BACKEND_KEY || e.key === docUrlKey) {
        checkLocalStorage()
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  // Check profile for document URL (cross-device detection)
  const profileCheckDone = useRef(false)
  useEffect(() => {
    // Only check profile once - avoid re-running when hasMigrated changes
    if (profileCheckDone.current) return
    profileCheckDone.current = true

    async function checkProfileMigration() {
      // If localStorage already indicates migration, no need to check profile
      if (hasMigrated) {
        setLoading(false)
        return
      }

      try {
        // Check if profile has a document URL for this environment (user migrated on another device)
        const { getCurrentOrigin } = await import('@/lib/environment')
        const origin = getCurrentOrigin()
        const profileDocUrl = await getAutomergeDocumentUrl(origin)

        if (profileDocUrl) {
          setHasMigrated(true)
          // Set backend flag to mark as migrated
          localStorage.setItem(STORAGE_BACKEND_KEY, 'automerge')
        }
      } catch (error) {
        console.error('Failed to check profile for migration status:', error)
        // Continue anyway - assume not migrated
      } finally {
        setLoading(false)
      }
    }

    checkProfileMigration()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run once on mount
  }, [])

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(MIGRATION_DISMISSED_KEY, 'true')
    setIsDismissed(true)
  }, [])

  return {
    hasMigrated,
    isDismissed,
    dismissPrompt,
    loading,
  }
}
